import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import { PersonService } from '../../services/personService';
import { User } from '../../types/auth';
import { Person } from '../../types/person';

/**
 * User Public Profile Page
 * Shows public information about a user account (not family tree person)
 */
const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const currentUser = authState.user;

  const [user, setUser] = useState<User | null>(null);
  const [linkedPerson, setLinkedPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user data
  const loadUserData = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      // Get user
      const userData = await UserService.getUserById(userId);

      if (!userData) {
        setError('User not found');
        setLoading(false);
        return;
      }

      setUser(userData);

      // Get linked person if exists
      if (userData.personId) {
        try {
          const personData = await PersonService.getPersonById(userData.personId);
          setLinkedPerson(personData);
        } catch (err) {
          console.error('Error loading linked person:', err);
          // Don't fail the whole page if person can't be loaded
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading user profile:', err);
      setError(err.message || 'Failed to load user profile');
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Check privacy settings
  const isOwnProfile = currentUser?.uid === userId;
  const isProfilePrivate = user?.preferences?.privacy?.profileVisibility === 'private';
  const canViewProfile = isOwnProfile || !isProfilePrivate;

  // Get role display info
  const getRoleInfo = (role: string) => {
    const roleMap: Record<string, { label: string; color: any }> = {
      admin: { label: 'Administrator', color: 'error' },
      editor: { label: 'Editor', color: 'primary' },
      contributor: { label: 'Contributor', color: 'secondary' },
      viewer: { label: 'Viewer', color: 'default' },
    };
    return roleMap[role] || { label: role, color: 'default' };
  };

  // Check field visibility
  const canShowField = (field: string): boolean => {
    if (isOwnProfile) return true; // Owner sees everything

    const fieldVisibility = user?.preferences?.privacy?.fieldVisibility;
    if (!fieldVisibility) return true; // Default to showing if not set

    const fieldMap: Record<string, keyof typeof fieldVisibility> = {
      email: 'showEmail',
      phone: 'showPhone',
      bio: 'showBio',
      location: 'showLocation',
      lastActive: 'showLastActive',
      photo: 'showProfilePhoto',
    };

    const visibilityKey = fieldMap[field];
    return visibilityKey ? fieldVisibility[visibilityKey] !== false : true;
  };

  // Format last active date
  const formatLastActive = (date: Date | undefined): string => {
    if (!date) return 'Never';

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Render loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'User not found'}
        </Alert>
      </Container>
    );
  }

  // Render private profile message
  if (!canViewProfile) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            This profile is private
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This user has chosen to keep their profile private.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const roleInfo = getRoleInfo(user.role);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Owner banner */}
      {isOwnProfile && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This is your public profile. Other users see this when they view your account.
          {isProfilePrivate && ' Your profile is currently set to private.'}
        </Alert>
      )}

      {/* Profile Header */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
          {/* Avatar */}
          {canShowField('photo') && (
            <Avatar
              src={user.photoURL}
              alt={user.displayName}
              sx={{ width: 120, height: 120 }}
            >
              {user.displayName.charAt(0).toUpperCase()}
            </Avatar>
          )}

          {/* Info */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="h4">
                {user.displayName}
              </Typography>
              {user.isEmailVerified && (
                <Chip
                  icon={<VerifiedIcon />}
                  label="Verified"
                  size="small"
                  color="success"
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              <Chip
                label={roleInfo.label}
                color={roleInfo.color}
                size="small"
              />
              {user.profileComplete && (
                <Chip label="Profile Complete" size="small" variant="outlined" />
              )}
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Member since {user.createdAt?.toLocaleDateString() || 'Unknown'}
                </Typography>
              </Stack>

              {canShowField('lastActive') && user.lastLoginAt && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Last active {formatLastActive(user.lastLoginAt)}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>

          {/* Edit button for owner */}
          {isOwnProfile && (
            <Button
              variant="outlined"
              onClick={() => navigate('/settings/profile')}
            >
              Edit Profile
            </Button>
          )}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* About Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Bio */}
              {canShowField('bio') && user.bio && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" paragraph>
                    {user.bio}
                  </Typography>
                </Box>
              )}

              {/* Contact Info */}
              <Stack spacing={2}>
                {canShowField('email') && user.email && (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <EmailIcon color="action" />
                    <Typography variant="body2">{user.email}</Typography>
                  </Stack>
                )}

                {canShowField('phone') && user.phoneNumber && (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <PhoneIcon color="action" />
                    <Typography variant="body2">{user.phoneNumber}</Typography>
                  </Stack>
                )}
              </Stack>

              {!user.bio && !canShowField('email') && !canShowField('phone') && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No additional information available.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Linked Person */}
          {linkedPerson && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Family Tree Profile
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <PersonIcon color="primary" />
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {linkedPerson.firstName} {linkedPerson.lastName}
                    </Typography>
                    {linkedPerson.birthDate && (
                      <Typography variant="body2" color="text.secondary">
                        Born {linkedPerson.birthDate}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate(`/people/${linkedPerson.id}`)}
                >
                  View Family Tree Profile
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Stats placeholder for future */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contributions
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Contribution statistics coming soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserProfilePage;
